// A reminder as the Reminders page shows it: real ones from the server's clock, plus sample ones for the design.
import { jordanDate, type Alarm, type VaultDate } from '@serb/shared';
import type { Tone } from '../../components/tone';
import { addDays, daysBetween } from '../../lib/dates';

export type ReminderType =
  'Reminder' | 'DG Approval' | 'Document' | 'Quotation' | 'Payment' | 'Pickup' | 'ETD' | 'ETA' | 'Follow-up';

/** Each kind of reminder has a colour: its dot in the calendar and the list */
export const TYPE_TONE: Record<ReminderType, Tone> = {
  Reminder: 'neutral',
  'DG Approval': 'error',
  Document: 'success',
  Quotation: 'info',
  Payment: 'warning',
  Pickup: 'success',
  ETD: 'info',
  ETA: 'success',
  'Follow-up': 'warning',
};

export type ReminderItem = {
  id: string;
  /** 'real' ones are on the server and can be deleted; 'vault' ones are dates from the Serb Mind vault (Claude keeps them up to date); 'sample' ones only exist for the design */
  source: 'real' | 'vault' | 'sample';
  title: string;
  srb: string;
  type: ReminderType;
  /** "2026-10-18" in Jordan time */
  date: string;
  /** "10:00", or "" when no time was set */
  time: string;
  relatedTo: string;
  createdBy: string;
  description: string;
  complete: boolean;
};

/** A real reminder from the server, in the page's shape. It is shown on the day it will arrive. */
export function fromAlarm(a: Alarm): ReminderItem {
  const when = new Date(a.deliverAt);
  const time = when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Amman' });
  return {
    id: a.id,
    source: 'real',
    title: a.title,
    srb: a.code,
    type: 'Reminder',
    date: jordanDate(when),
    time,
    relatedTo: '—',
    createdBy: a.by || 'Ibraheem',
    description: a.note,
    complete: false,
  };
}

const VAULT_TYPE: Record<VaultDate['kind'], ReminderType> = {
  'Next step': 'Follow-up',
  ETD: 'ETD',
  ETA: 'ETA',
  'Quotation valid until': 'Quotation',
  'Invoice due': 'Payment',
};

/** A date from the vault, in the page's shape */
export function fromVaultDate(v: VaultDate): ReminderItem {
  return {
    id: `vault-${v.id}`,
    source: 'vault',
    title: v.title,
    srb: v.code,
    type: VAULT_TYPE[v.kind],
    date: v.date,
    time: '',
    relatedTo: v.who || '—',
    createdBy: 'Vault',
    description: v.note,
    complete: v.done,
  };
}

/** Sample reminders, placed around today so the calendar always has something to show */
export function sampleReminders(today: string): ReminderItem[] {
  const mk = (
    offset: number,
    time: string,
    title: string,
    srb: string,
    type: ReminderType,
    relatedTo: string,
    by: string,
    description: string,
  ): ReminderItem => ({
    id: `s-${srb}-${offset}`,
    source: 'sample',
    title,
    srb,
    type,
    date: addDays(today, offset),
    time,
    relatedTo,
    createdBy: by,
    description,
    complete: false,
  });
  return [
    mk(
      -2,
      '',
      'DG approval deadline',
      'SRB-518',
      'DG Approval',
      'Customer',
      'Ibraheem',
      'Dangerous goods approval is due. Chase the carrier.',
    ),
    mk(
      0,
      '10:00',
      'Confirm pickup',
      'SRB-640',
      'Pickup',
      'Overseas Agent',
      'Ibraheem',
      'Confirm pickup arrangement and truck details with supplier.',
    ),
    mk(
      0,
      '15:00',
      'Agent BL',
      'SRB-625',
      'Document',
      'Overseas Agent',
      'Ibraheem',
      'Get the draft bill of lading from the agent.',
    ),
    mk(
      2,
      '10:00',
      'Agent BL submission',
      'SRB-625',
      'Document',
      'Overseas Agent',
      'Ibraheem',
      'The agent submits the bill of lading.',
    ),
    mk(
      4,
      '',
      'Send quotation to customer',
      'SRB-711',
      'Quotation',
      'Customer',
      'Ibraheem',
      'Send the approved quotation PDF.',
    ),
    mk(
      5,
      '',
      'Customer payment follow-up',
      'SRB-596',
      'Payment',
      'Customer',
      'Sokaina',
      'Payment is due. Ask the customer for the transfer slip.',
    ),
    mk(
      9,
      '10:00',
      'Confirm pickup with supplier',
      'SRB-640',
      'Pickup',
      'Overseas Agent',
      'Ibraheem',
      'Confirm pickup arrangement and truck details with supplier.',
    ),
    mk(13, '', 'ETD (Messina Line)', 'SRB-539', 'ETD', 'Overseas Agent', 'Ibraheem', 'Vessel departs.'),
    mk(-5, '10:00', 'Pickup (Supplier)', 'SRB-625', 'Pickup', 'Overseas Agent', 'Ibraheem', 'Pickup at the supplier.'),
  ].map((r, i) => (i === 8 ? { ...r, complete: true } : r));
}

export type ReminderStatus = { label: string; tone: Tone };

/** Overdue, Today, In N days, Scheduled (later), Completed */
export function statusOf(r: ReminderItem, today: string): ReminderStatus {
  if (r.complete) return { label: 'Completed', tone: 'success' };
  const days = daysBetween(today, r.date);
  if (days < 0) return { label: 'Overdue', tone: 'error' };
  if (days === 0) return { label: 'Today', tone: 'warning' };
  if (days <= 14) return { label: `In ${days} days`, tone: 'warning' };
  return { label: 'Scheduled', tone: 'info' };
}
